import Vue from 'vue'
import {
    ICollectionTask,
    EResidentStatus,
    EUserPermissions,
    IResident,
    IResidentContact,
    ITemplate,
    IUser,
    EPaymentStatus,
    EPaymentType,
    ESchedulePaymentSettingStatus,
    ETransactionEffectiveDateStatus,
    IResidentEventHistoryFiltersDTO,
    ESchedulePaymentItemStatus,
    ECollectionHistoryType,
    IFacility
} from '@zunta/configurations'
import { NoCache } from '@/decorators/NoCache'
import { ISelectItem } from '@/interfaces'
import Component from 'vue-class-component'
import { User } from '@/classes/User'
import Api from '@/api'
import { ResidentModule } from '@/store/modules/resident.module'
import { getModule } from 'vuex-module-decorators'
import { TransactionsModule } from '@/store/modules/transactions.module'
import { UserModule } from '@/store/modules/user.module'
import { TemplateModule } from '@/store/modules/template.module'

import { Debounce } from 'vue-debounce-decorator'
import { PaymentsModule } from '@/store/modules/payments.module'
import { formatDate } from '@/constants'
import { FacilityModule } from '@/store/modules/facility.module'

@Component({})
export default class CollectionSharedFunctions extends Vue {
    formatDate: any = formatDate

    user = Api.getUser() as User

    EPaymentStatus: any = EPaymentStatus
    EResidentStatus: any = EResidentStatus
    EUserPermissions: any = EUserPermissions
    EPaymentType: any = EPaymentType
    ESchedulePaymentSettingStatus: any = ESchedulePaymentSettingStatus
    ESchedulePaymentItemStatus: any = ESchedulePaymentItemStatus
    ETransactionEffectiveDateStatus: any = ETransactionEffectiveDateStatus
    paymentsModuleInstance: PaymentsModule = getModule(PaymentsModule, this.$store)
    residentModuleInstance: ResidentModule = getModule(ResidentModule, this.$store)
    facilityModuleInstance: FacilityModule = getModule(FacilityModule, this.$store)
    transactionsModuleInstance: TransactionsModule = getModule(TransactionsModule, this.$store)
    userModuleInstance: UserModule = getModule(UserModule, this.$store)
    templateModuleInstance: TemplateModule = getModule(TemplateModule, this.$store)

    noResidentsInCollections: boolean = false
    noTasksModal: boolean = false
    showSuccessfulModal: boolean = false

    residentHistoryFilters: IResidentEventHistoryFiltersDTO = {
        type: ECollectionHistoryType.COLLECTION
    }

    cache: any = {
        previousResidentId: null
    }

    get facilityCollectionSettings () {
        return this.$store.state.facility.facilityCollectionSettings
    }

    get collectionsHistory () {
        return this.$store.state.resident.collectionHistory
    }

    get templatesList () {
        return this.$store.state.template.templates
    }

    get usersList () {
        return this.$store.state.user.users
    }

    get userListItems () {
        const facilityId = this.selectedTaskFacilityId
        let items
        if (facilityId) {
            items = this.usersList.filter((user: IUser) => (
                user.isAllFacilities || user.facilities.some(f => f.toString() === facilityId.toString()))
            )
        } else {
            items = this.usersList
        }
        return items.map((user: IUser) => ({
                key: user.id,
                value: user.id,
                content: `${user.firstName} ${user.lastName}`
            }))
    }

    get templateListItems () {
        return this.templatesList.map((template: ITemplate) => {
            return {
                content: template.templateName,
                value: template.id,
                key: template.id
            }
        })
    }

    get residentContacts () {
        return this.$store.state.resident.residentContacts.map((item: any) => item).sort((a: IResidentContact) => a.isPrimary ? -1 : 0)
    }

    get contactsListItems () {
        return this.residentContacts.map((residentContact: IResidentContact) => {
            return {
                content: `${residentContact.firstName} ${residentContact.lastName}`,
                value: residentContact.id,
                key: residentContact.id
            }
        })
    }

    getTransactionStatusLabel (status: ETransactionEffectiveDateStatus) {
        const TRANSACTIONS_STATUS_LABEL: any = {
            [ETransactionEffectiveDateStatus.BILLED]: 'Billed',
            [ETransactionEffectiveDateStatus.OVERDUE]: 'Overdue',
            [ETransactionEffectiveDateStatus.DUE]: 'Due',
            [ETransactionEffectiveDateStatus.DELINQUENT]: 'Delinquent',
            [ETransactionEffectiveDateStatus.PAID]: 'Paid in Full',
            [ETransactionEffectiveDateStatus.WRITTEN_OFF]: 'Written Off',
            [ETransactionEffectiveDateStatus.WRITTEN_OFF_AND_CEASE_COLLECTIONS]: 'Written Off & Cease'
        }
        return TRANSACTIONS_STATUS_LABEL[status]
    }

    get transactionsStatusList (): ISelectItem[] {
        return [{
            key: 'all',
            value: undefined as any,
            content: 'All'
        }].concat(Object.values(ETransactionEffectiveDateStatus).map((status: ETransactionEffectiveDateStatus) => ({
            key: status,
            value: status,
            content: this.getTransactionStatusLabel(status)
        })))
    }

    get residentTransactions () {
        return this.$store.state.transactions.transactions
    }

    get collectionTasks () {
        // if (this.type === 'today') {
        //     return this.$store.state.resident.collectionTasks.filter((task: ICollectionTask) => {
        //         return (task.assignedUser as IUser).id === this.user.data.id
        //     })
        // }
        return this.$store.state.resident.collectionTasks
    }

    @NoCache
    get currentTaskIndex () {
        return this.collectionTasks.indexOf(this.selectedTask)
    }

    @NoCache
    get selectedTask () {
        return this.collectionTasks.find((task: ICollectionTask) => {
            return (task.resident as IResident).id === this.residentId
        })
    }

    @NoCache
    get selectedResident () {
        if (!this.selectedTask) {
            return null
        }
        return this.selectedTask.resident
    }

    get residentOpenBalance () {
        return this.$store.state.resident.openBalance
    }

    get type () {
        return this.$route.query.type
    }

    get selectedTaskFacility (): IFacility | null {
      if (!this.selectedTask) {
        return null
      }
      return this.selectedTask.facility
    }

    get selectedTaskFacilityId () {
        return this.selectedTaskFacility ? (this.selectedTaskFacility.id as number) : null
    }

    get residentId () {
        return Number(this.$route.params.residentId)
    }

    get ableToGoNext () {
        return this.currentTaskIndex < this.collectionTasks.length - 1
    }

    get ableToGoPrev () {
        return this.currentTaskIndex > 0
    }

    async updateResidentInfo () {
        if (this.residentId !== this.cache.previousResidentId && this.selectedResident) {
            this.cache.previousResidentId = this.residentId
            this.$eventBus.$emit('CollectionView:loadHistory', this.residentId)
            await Promise.all([
                this.residentModuleInstance.fetchResidentContacts(this.residentId),
                this.residentModuleInstance.fetchResidentOpenBalance(this.residentId),
                this.transactionsModuleInstance.getGroupedPaymentsTotals(this.residentId),
                this.facilityModuleInstance.getFacilityCollectionSettings(this.selectedResident.facility)
            ])
        }
    }

    @Debounce(300)
    async debounceUpdate (settingName: string, value: any, residentId?: number) {
        await this.changeResidentSetting(settingName, value, residentId)
    }

    async changeResidentSetting (settingName: string, value: any, residentId?: number) {
        const actionAllowed = this.user.hasPermissionFor(this.EUserPermissions.EDIT_RESIDENT_SETTINGS) ||
            (this.user.isResident() && settingName === 'paperlessStatement')
        if (actionAllowed) {
            await this.residentModuleInstance.updateResidentSettings({
                residentId: residentId || this.residentId,
                settings: {
                    [settingName]: value
                }
            })
        } else {
            this.$eventBus.$emit('notify', {
                color: 'var(--status-error-color)',
                message: 'Permission Denied',
                header: `Modify Resident Settings`,
                icon: 'warning'
            })
        }
    }

    async goNextTask () {
        if (this.ableToGoNext) {
            this.$router.push({
                name: 'management-collection-resident',
                params: {
                    residentId: (this.collectionTasks[this.currentTaskIndex + 1].resident as IResident).id.toString()
                },
                query: {
                    type: this.type
                }
            })
        } else {
            if (this.type === 'today') {
                const count = await this.residentModuleInstance.fetchCollectionTasksCountByFilters({
                    type: 'today',
                    active: true
                })
                if (!count) {
                    this.showSuccessfulModal = true
                    return
                }
            }
            this.$router.push({
                name: 'management-collection-resident',
                params: {
                    residentId: (this.collectionTasks[0].resident as IResident).id.toString()
                },
                query: {
                    type: this.type
                }
            })
        }
    }

    goPrevTask () {
        if (this.ableToGoPrev) {
            this.$router.push({
                name: 'management-collection-resident',
                params: {
                    residentId: (this.collectionTasks[this.currentTaskIndex - 1].resident as IResident).id.toString()
                },
                query: {
                    type: this.type
                }
            })
        }
    }

    goBack () {
        this.$locationManager.goBack({ name: 'management-collections' })
    }

    goToResident (options: any) {
      this.$locationManager.openNewTab(this.$router.resolve(options).href)
    }
}
